/**
 * Editing Viewport Adjustment
 *
 * Pure computation for camera adjustments during inline editing.
 * When a node grows (e.g., typing a long headline), this computes
 * the camera change needed to keep the node fully visible.
 */

/** Minimal camera state */
interface Camera {
  x: number;
  y: number;
  zoom: number;
}

/** Input for the viewport adjustment computation */
export interface EditingViewportInput {
  /** Node bounds in world coordinates (top-left origin) */
  node: { x: number; y: number; width: number; height: number };
  /** Current camera (center of viewport in world coordinates + zoom) */
  camera: Camera;
  /** Viewport size in screen pixels */
  viewport: { width: number; height: number };
  /**
   * Distance from viewport edge (in screen px) at which adjustment triggers.
   * Default: 60
   */
  triggerMargin?: number;
  /**
   * Target distance from viewport edge (in screen px) after adjustment.
   * Default: 150
   */
  targetMargin?: number;
  /** Minimum allowed zoom level. Default: 0.1 */
  minZoom?: number;
}

/**
 * Compute camera adjustment to keep a node visible during editing.
 *
 * Returns a new Camera if adjustment is needed, or null if the node
 * is already comfortably within the viewport.
 *
 * Two-threshold system:
 * - triggerMargin: when a node edge is within this many screen px of the
 *   viewport edge, an adjustment is triggered
 * - targetMargin: the adjustment pans to put the edge at this distance
 *   from the viewport edge (giving breathing room)
 *
 * If the node is wider or taller than the viewport (minus margins),
 * the camera zooms out to fit, centered on the node.
 */
export function computeEditingCameraAdjustment(
  input: EditingViewportInput,
): Camera | null {
  const {
    node,
    camera,
    viewport,
    triggerMargin = 60,
    targetMargin = 150,
    minZoom = 0.1,
  } = input;

  const zoom = camera.zoom;

  // Node edges in world coordinates
  const nodeLeft = node.x;
  const nodeTop = node.y;
  const nodeRight = node.x + node.width;
  const nodeBottom = node.y + node.height;

  // Viewport bounds in world coordinates
  const vpHalfW = viewport.width / zoom / 2;
  const vpHalfH = viewport.height / zoom / 2;
  const vpLeft = camera.x - vpHalfW;
  const vpTop = camera.y - vpHalfH;
  const vpRight = camera.x + vpHalfW;
  const vpBottom = camera.y + vpHalfH;

  // Trigger and target margins in world coordinates
  const triggerW = triggerMargin / zoom;
  const targetW = targetMargin / zoom;

  // Check if node is larger than viewport (minus target margins on both sides)
  const availableW = (viewport.width - targetMargin * 2) / zoom;
  const availableH = (viewport.height - targetMargin * 2) / zoom;

  if (node.width > availableW || node.height > availableH) {
    // Need to zoom out to fit the node
    const nodeCX = nodeLeft + node.width / 2;
    const nodeCY = nodeTop + node.height / 2;
    const padding = targetMargin;
    const zoomW = (viewport.width - padding * 2) / node.width;
    const zoomH = (viewport.height - padding * 2) / node.height;
    const newZoom = Math.max(Math.min(zoomW, zoomH), minZoom);

    // Only adjust if we actually need to zoom out (or if it's significantly off)
    if (newZoom < zoom * 0.98) {
      return { x: nodeCX, y: nodeCY, zoom: newZoom };
    }
  }

  // Check each edge against trigger margin (pan only)
  let dx = 0;
  let dy = 0;

  // Right edge too close to right side of viewport
  if (nodeRight > vpRight - triggerW) {
    dx = nodeRight - (vpRight - targetW);
  }
  // Left edge too close to left side of viewport
  if (nodeLeft < vpLeft + triggerW) {
    dx = nodeLeft - (vpLeft + targetW);
  }
  // Bottom edge too close to bottom of viewport
  if (nodeBottom > vpBottom - triggerW) {
    dy = nodeBottom - (vpBottom - targetW);
  }
  // Top edge too close to top of viewport
  if (nodeTop < vpTop + triggerW) {
    dy = nodeTop - (vpTop + targetW);
  }

  if (dx === 0 && dy === 0) return null;

  return { x: camera.x + dx, y: camera.y + dy, zoom };
}
